import { Add } from "@mui/icons-material";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  LinearProgress,
} from "@mui/material";
import { NewPriceRow } from "./price-row/NewPriceRow";
import { PriceRow } from "./price-row/PriceRow";
import { useMemo, useRef, useState } from "react";
import { useCustom } from "@refinedev/core";
import { ShipmentPrice } from "../types";
import { useParams } from "react-router";
import { useTranslation } from "../../../../hooks/useTranslation";

export const PriceList = () => {
  const { translate } = useTranslation("pages.shipmentPrices.details");

  const { countryId, carrierId, serviceId } = useParams<{
    countryId: string;
    carrierId: string;
    serviceId: string;
  }>();

  const newRowRef = useRef<HTMLDivElement>(null);
  const [editingRuleId, setEditingRuleId] = useState<string | undefined>(
    undefined
  );

  const { data, isLoading } = useCustom<ShipmentPrice[]>({
    url: `shipment-prices`,
    method: "get",
    meta: {
      query: {
        countryId: countryId,
        carrierId: carrierId,
        carrierServiceId: serviceId,
        size: 1000,
      },
    },
  });

  // This removes any items with undefined or null desi values,
  // fills in missing desi values with a template item, and sorts the result.
  // It ensures that the desi values are continuous from the minimum to the maximum.
  const shipmentPrices = useMemo(() => {
    const shipmentPriceResponse = data?.data ?? [];
    const filtereedResponse = shipmentPriceResponse.filter(
      (item) => item.desi !== undefined && item.desi !== null
    );

    if (filtereedResponse.length === 0) return [];

    const desiValues = filtereedResponse.map((item) => item.desi);
    const minDesi = Math.min(...desiValues);
    const maxDesi = Math.max(...desiValues);

    const desiMap = new Map<number, ShipmentPrice>();
    filtereedResponse.forEach((item) => {
      desiMap.set(item.desi, item);
    });

    const filled: ShipmentPrice[] = [];
    for (let desi = minDesi; desi <= maxDesi; desi++) {
      if (desiMap.has(desi)) {
        filled.push(desiMap.get(desi)!);
      } else {
        const template = filtereedResponse[0];
        filled.push({
          ...template,
          id: 0 - desi,
          desi,
          price: 0,
        });
      }
    }

    return (
      filled.toSorted((a, b) => {
        if (a.desi === b.desi) {
          return (a.price ?? 0) - (b.price ?? 0);
        }
        return a.desi - b.desi;
      }) ?? []
    );
  }, [data]);

  const getBackgroundColor = (index: number, id: number) => {
    const alternatingColor =
      index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "transparent";

    return id < 0 ? "rgba(255, 0, 0, 0.19)" : alternatingColor;
  };

  if (isLoading) {
    return <LinearProgress />;
  }

  return (
    <>
      <TableContainer
        sx={{
          borderRadius: 1,
          overflowY: "auto",
          maxHeight: "calc(100vh - 300px)",
          border: (theme) => `1px solid ${theme.palette.customBackground.box}`,
        }}
      >
        <Table>
          <TableHead
            sx={{
              backgroundColor: (theme) => theme.palette.customBackground.box,
              position: "sticky",
              top: "-1px",
              zIndex: 1,
            }}
          >
            <TableRow>
              <TableCell align="center" width="170px">
                {translate("fields.desi")}
              </TableCell>
              <TableCell align="center" width="170px">
                {translate("fields.price")}
              </TableCell>
              <TableCell align="center">
                {translate("fields.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shipmentPrices.map((item, index) => (
              <PriceRow
                key={item.id}
                price={item}
                onEditStart={() => setEditingRuleId(item.id.toString())}
                isEditing={editingRuleId === item.id.toString()}
                onEditStop={() => setEditingRuleId(undefined)}
                backgroundColor={getBackgroundColor(index, item.id)}
              />
            ))}
            {editingRuleId === "newRule" && (
              <NewPriceRow onClose={() => setEditingRuleId(undefined)} />
            )}
            <div ref={newRowRef} />
          </TableBody>
        </Table>
      </TableContainer>
      <Button
        onClick={() => {
          setEditingRuleId("newRule");
          setTimeout(() => {
            newRowRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
            });
          }, 0);
        }}
        variant="text"
        disableElevation
        size="large"
        disabled={editingRuleId === "newRule"}
        startIcon={<Add />}
        sx={{
          borderRadius: "0px",
          border: (theme) => `1px solid ${theme.palette.customBackground.box}`,
        }}
        fullWidth
      >
        {translate("addNew")}
      </Button>
    </>
  );
};
